import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  HStack,
  Container,
  Divider,
  Collapse,
  IconButton,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { TbAlertCircle } from 'react-icons/tb';
import { FaRocketchat } from 'react-icons/fa';
import Outline from './components/Outline';
import PromptCard from './components/PromptCard';
import ChatBox from 'components/ChatBox';
import theme from 'ui/theme/theme';
import { Wrapper } from './style/WritingPracticePage.styles';
import { instr_planning } from 'utils/data/constants';
import InactiveBanner from './components/InactiveBanner';
import SmallScreenAlert from './components/SmallScreenAlert';
import { essay_schema } from './data/essay_schema';

const QUERY_INTERVAL = 20;

function PlanningForm({
  breadcrumbProg,
  setBreadcrumbProg,
  nextPath,
  returnUrl,
  isPlanActive,
}) {
  // console.log('-------------------------------entering planning page');

  const navigate = useNavigate();
  const { essayId } = useParams();
  const toast = useToast();
  const isNarrow = useBreakpointValue({ base: true, md: true, lg: false });
  const [exerciseData, setExerciseData] = useState({});
  const [outlines, setOutlines] = useState([]);
  const [promptData, setPromptData] = useState({});
  const [enableNext, setEnableNext] = useState(false);

  const [messages, setMessages] = useState([]);
  const [lastQueryTime, setLastQueryTime] = useState(
    Math.round(Date.now() / 1000 - QUERY_INTERVAL)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);

  const essayDetails = {
    ...essay_schema,
    ...JSON.parse(localStorage.getItem('essayExerciseDetails')),
  };

  function updateStateData(details) {
    setExerciseData(details);
    setOutlines(details.outlines);
    setPromptData({
      essayTitle: details.essayTitle,
      essayType: details.secondaryType || details.primaryType,
      essayPrompt: details.prompt,
      url: details.illustration,
      level: details.level,
      minWordCount: details.minWordCount,
    });
    setEnableNext(details.progress > 2 ? true : false);
  }

  useEffect(() => {
    setBreadcrumbProg(breadcrumbProg);
    // setOutlines(data?.outlines);
  }, [setBreadcrumbProg, breadcrumbProg]);

  useEffect(() => {
    if (essayDetails?.essayId == essayId) {
      updateStateData(essayDetails);
    }
    if (
      essayDetails?.essayId != essayId ||
      (essayDetails.progress > 1 &&
        (!essayDetails?.outlines.length || !essayDetails?.outlinesFixed))
    ) {
      api
        .get(`${API_BASE}/api/essay-exercise/retrieve-data?exerciseId=${essayId}`)
        .then((res) => {
          // console.log(
          //   'retrieve essay details from planning page, res: =====',
          //   res
          // );
          if (res.status == 200) {
            if (res.data?.code == 200) {
              const newExerciseData = res.data?.data;
              updateStateData(newExerciseData);
              localStorage.setItem(
                'essayExerciseDetails',
                JSON.stringify(newExerciseData)
              );
            }
          }
        })
        .catch(() =>
          console.log('failed to fetch essay exercise, id: ', essayId)
        );
    }
  }, []);

  useEffect(() => {
    api
      .get(`${API_BASE}/api/chat/exercise-chats?exerciseId=${essayId}&section=2`)
      .then((res) => {
        if (res.status == 200) {
          setMessages(res.data?.data);
        }
      });
  }, [essayId]);

  const toggleChatbox = () => setIsChatOpen((prev) => !prev);

  function handleOutlineChange(answer, idx, type) {
    const body = {
      exerciseId: essayId,
      outlineIndex: idx,
      answer: answer,
      updateType: type,
    };
    // console.log('=======patch outlines request body: ', body);

    if ((!answer || answer.length < 20) && type == 2) {
      const warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
      toastMessage(warning);
      return;
    }

    // if (!skipMessageToast(answer, type > 1)) {
    //   return;
    // }
    if (type > 1) {
      setLastQueryTime(Math.round(Date.now() / 1000 - QUERY_INTERVAL));
    }

    setIsSubmitting(true);
    api
      .patch(`${API_BASE}/api/essay-exercise/outlines`, body)
      .then((res) => {
        // console.log('=======patch outlines response: ', res);
        if (res.status == 200) {
          const responseData = res.data?.data;
          setOutlines(responseData?.outlines);
          setEnableNext(responseData?.progress > 2 ? true : false);
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(responseData)
          );
        }
      })
      .catch(() => toastMessage('Failed to update outlines.'))
      .finally(() => setIsSubmitting(false));
  }

  const handleAssistantChat = async (answer, idx, type) => {
    const body = {
      exerciseId: essayId,
      outlineIndex: idx,
      currentWork: answer,
      chatType: type,
      writingPrompt: exerciseData?.prompt,
      essayType: exerciseData.secondaryType || exerciseData.primaryType,
      grade: exerciseData?.level,
      tips: parseOutlineTips(idx),
    };

    const currTimeSec = Date.now() / 1000;
    let warning = '';
    if (currTimeSec - lastQueryTime < QUERY_INTERVAL) {
      warning =
        'Take a moment to review your work before submitting. We believe in your best effort!';
    }
    if ((!answer || answer.length < 20) && type == 3) {
      warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
    }
    if (warning) {
      toastMessage(warning);
      return;
    }
    setIsChatOpen(true);

    // if (!skipMessageToast(answer, false)) {
    //   return;
    // }
    setLastQueryTime(currTimeSec);
    // console.log('=======planning assistant body: ', body);
    setIsSubmitting(true);
    api
      .post(`${API_BASE}/api/chat/planning-assistant`, body)
      .then((res) => {
        // console.log('=======planning assistant res: ', res);
        if (res.status == 200) {
          updateChatMessage(res.data?.data);
          // todo: update local message storage ?
        }
      })
      .catch(() => toastMessage('Sorry, Ivy is busy.'))
      .finally(() => setIsSubmitting(false));
  };

  function toastMessage(msg) {
    toast({
      position: 'top',
      render: () => (
        <Box
          border="1px solid"
          // color={theme.colors.semantics['warning-light']}
          borderColor={theme.colors.semantics.warning}
          p={4}
          bg={theme.colors.semantics['warning-light']}
          borderRadius={12}
          wordBreak="break-all"
        >
          <Flex>
            <TbAlertCircle color={theme.colors.semantics.warning} />
            <Flex ml="12px" flex="1">
              <Text fontWeight="bold" ml="2">
                {msg}
              </Text>
            </Flex>
          </Flex>
        </Box>
      ),
      duration: 3000,
    });
  }

  function updateChatMessage(chats) {
    let newMessages = [];
    messages.forEach((msg) => newMessages.push(msg));
    chats.forEach((c) => newMessages.push(c));
    setMessages(newMessages);
  }

  function parseOutlineTips(idx) {
    let tips = [];
    if (outlines && outlines.length > idx) {
      tips = outlines[idx].tips;
    }
    return tips;
  }

  if (isNarrow) {
    return <SmallScreenAlert />;
  }

  return (
    <Container width={isChatOpen ? '95%' : '75%'} maxW="none">
      {!isPlanActive && <InactiveBanner />}
      {/* <HStack align="stretch" spacing={0}> */}
      <Flex height="calc(100vh - 48px)" overflow="hidden">
        <Box
          flex="2" // 70% of the width
          overflowY="auto" // Independent scroll for this section
          p={4}
        >
          <Flex flexDirection="column" marginBottom="16px">
            <PromptCard promptData={promptData} defaultShow={false} />
            <Heading size="md" color="teal.700">
              Planning & Outlines
            </Heading>
            <Divider mt={2} mb={4} />
            <Box>
              {outlines &&
                outlines.map((outline, idx) => {
                  return outline.status === 'Hidden' ? null : (
                    <Outline
                      key={idx}
                      idx={idx}
                      outline={outline}
                      isSubmitting={isSubmitting}
                      updateOutline={handleOutlineChange}
                      assistantChat={handleAssistantChat}
                    />
                  );
                })}
            </Box>
          </Flex>
          <HStack spacing={4} mb={4} justify="flex-end">
            <Button
              colorScheme="gray"
              type="button"
              variant="link"
              onClick={() => navigate(returnUrl)}
            >
              Back
            </Button>
            <Button
              colorScheme="green"
              type="button"
              onClick={() => navigate(nextPath)}
              isDisabled={!enableNext}
            >
              Next
            </Button>
          </HStack>
        </Box>
        <Collapse in={isChatOpen}>
          <Box
            flex="1"
            // position="fixed"
            // bottom="32px"
            // right="32px"
            // overflow="hidden"
            overflowY="auto"
            border="1px solid lightgray"
            borderRadius="md"
            backgroundColor="white"
            padding={2}
            mb={2}
            boxShadow="lg"
            transition="min-width 0.3s ease-in-out"
            zIndex="10"
          >
            <HStack justify="space-between">
              <Box fontSize="lg" fontWeight="bold">
                Chat
              </Box>
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                onClick={toggleChatbox}
                aria-label="Close chat"
              />
            </HStack>
            <Wrapper padding="0px 0px 0px 0px">
              <ChatBox
                messages={messages}
                preMessage={instr_planning}
                essayId={essayId}
                prompt={prompt}
                grade={exerciseData?.level}
                essayType={
                  exerciseData.secondaryType || exerciseData.primaryType
                }
                section={2}
                flexibleChat={updateChatMessage}
              />
            </Wrapper>
          </Box>
        </Collapse>
        {/* Toggle Button (if chat is hidden) */}
        {!isChatOpen && (
          <IconButton
            icon={<FaRocketchat color="#2d8645" />}
            position="fixed" // Keep toggle button fixed
            top={24}
            right={16}
            onClick={toggleChatbox}
            aria-label="Open chat"
            boxSize="36px" // Adjust the button size
            fontSize="32px" // Adjust the icon size
            background="transparent"
            _hover={{ background: 'transparent', color: 'blue.500' }}
          />
        )}
      </Flex>
    </Container>
  );
}

PlanningForm.propTypes = {
  breadcrumbProg: PropTypes.number,
  setBreadcrumbProg: PropTypes.func,
  nextPath: PropTypes.string,
  returnUrl: PropTypes.string,
  isPlanActive: PropTypes.bool,
};

export default PlanningForm;
